/*
 * NodePalette — left sidebar with draggable node types grouped by category.
 * Fetches node type catalog from the API and renders draggable cards
 * with SVG icons and category accent stripes.
 */

import React, { useState, useEffect } from 'react';
import { captureAPI } from '../../services/api';
import { getNodeIcon } from './nodes/NodeIcons';
import './NodePalette.css';

const CATEGORY_ORDER = ['trigger', 'processing', 'routing', 'action', 'output'];

const CATEGORY_LABELS = {
  trigger: 'Triggers',
  processing: 'Processing',
  routing: 'Routing',
  action: 'Actions',
  output: 'Outputs',
};

const NodePalette = ({ collapsed, onToggleCollapse }) => {
  const [nodeTypes, setNodeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCategories, setOpenCategories] = useState(
    () => new Set(CATEGORY_ORDER)
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await captureAPI.workflows.getNodeTypes();
        if (!cancelled && res.ok) {
          setNodeTypes(res.nodeTypes || []);
        }
      } catch (err) {
        console.error('[NodePalette] Failed to load node types:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleCategory = (cat) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow-type', JSON.stringify(nodeType));
    event.dataTransfer.effectAllowed = 'move';
  };

  // Group by category
  const grouped = {};
  for (const nt of nodeTypes) {
    const cat = nt.category || 'action';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(nt);
  }

  return (
    <div className={`np-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="np-sidebar-header">
        {!collapsed && <span className="np-sidebar-title">Node Palette</span>}
        <button
          className="np-collapse-btn"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand palette' : 'Collapse palette'}
        >
          {collapsed ? '▶' : '◀'}
        </button>
      </div>

      {!collapsed && (
        <div className="np-body">
          {loading ? (
            <div className="np-loading">Loading nodes...</div>
          ) : (
            CATEGORY_ORDER.map(cat => {
              const items = grouped[cat];
              if (!items || items.length === 0) return null;
              const isOpen = openCategories.has(cat);
              return (
                <div className="np-category" key={cat}>
                  <div
                    className={`np-category-header np-cat-${cat}`}
                    onClick={() => toggleCategory(cat)}
                  >
                    <span className={`np-category-chevron ${isOpen ? 'open' : ''}`}>▶</span>
                    <span>{CATEGORY_LABELS[cat] || cat}</span>
                  </div>
                  {isOpen && (
                    <div className="np-category-items">
                      {items.map(nt => {
                        const IconComponent = getNodeIcon(nt.type);
                        return (
                          <div
                            key={nt.type}
                            className={`np-node-card np-card-${nt.category || cat}`}
                            draggable
                            onDragStart={(e) => onDragStart(e, nt)}
                            title={nt.description}
                          >
                            <span className="np-node-icon"><IconComponent /></span>
                            <div className="np-node-info">
                              <div className="np-node-label">{nt.label}</div>
                              <div className="np-node-desc">{nt.description}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default NodePalette;
